import { markdownToExcerpt } from '@littlebird/markdown-utils';
import { useStores } from '@littlebird/sdk';
import { type MeetingCircle } from '@littlebird/ui-utils';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns/format';
import { isToday } from 'date-fns/isToday';
import { isTomorrow } from 'date-fns/isTomorrow';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { SidebarSectionHeading } from '@/components/blocks/Layout/Sidebar/SidebarSectionContainer';
import { SECTION_GAP } from '@/components/blocks/Layout/Sidebar/style';
import {
  AnimatedAudiowave,
  AnimatedTimer,
  Time,
  XStack,
  YStack,
} from '@/components/ui';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { EntityIcon } from '@/components/ui/icon-picker';
import { RoundButton } from '@/design-system-v2/round-button';
import { RoundIconButton } from '@/design-system-v2/round-icon-button';
import { SidePanel } from '@/design-system-v2/side-panel';
import { SidebarRow } from '@/design-system-v2/sidebar-row';
import { electronApis } from '@/features/electron-api';
import { getProjectSidebarIcon } from '@/features/projects/project-sidebar-icon';
import { useLoadAllProjectPages } from '@/features/projects/use-load-all-project-pages';
import { openSettings } from '@/features/settings/settings-navigation';
import { isNewTabClick, useTabs } from '@/features/tabs/tabs';
import { performScheduleJoin } from '@/features/widgets/schedule';
import { pluralize } from '@/lib/date-utils';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { AppCapability, inElectron, isCapable } from '@/utils/capabilities';

import { useEnsureProjectLoaded } from './meeting-project-chip';
import { CircleFaces } from './meetings-common.components';
import { MEETING_PREP_READY_SEARCH } from './meetings-prep-visibility';
import {
  meetingsSidebarStore,
  NO_PROJECT_SLICE,
} from './meetings-sidebar.store';
import {
  type AgendaItem,
  isNewNoteDisabled,
  startMeetingFromOverview,
  useHideMeetingCircle,
  useMeetingCircles,
  useMeetingsAgenda,
} from './meetings.hooks';
import { ensurePrepMeetingForCalendarEvent } from './meetings.query';
import { meetingStore } from './meetings.state';
import {
  type CalendarEvent,
  type MeetingAudioEnergyLevel,
} from './meetings.types';

const JOIN_WINDOW_MS = 10 * 60_000;
const SECTION_LIMIT = 5;
const SECTION_PAGE = 10;
const NOW_TICK_MS = 30_000;

const onActivateKey =
  (activate: () => void) => (event: React.KeyboardEvent) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    activate();
  };

const getAgendaItemMeeting = (
  item: AgendaItem,
  meetings: ReturnType<typeof useStores>['meetings']
) =>
  item.existingMeeting ??
  (item.event ? meetings.getMeetingByEventId(item.event.id) : undefined) ??
  (item.meetingId ? meetings.get(item.meetingId) : undefined);

const useNow = () => {
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), NOW_TICK_MS);
    return () => clearInterval(interval);
  }, []);

  return now;
};

const useOpenAgendaItem = () => {
  const navigate = useNavigate();
  const { openTab } = useTabs();

  return async (
    item: AgendaItem,
    event?: React.MouseEvent,
    { openPrep = false }: { openPrep?: boolean } = {}
  ) => {
    const meetingId = item.event
      ? await ensurePrepMeetingForCalendarEvent(
          item.event,
          item.existingMeeting
        )
      : item.meetingId;
    if (!meetingId) return;

    if (!item.event) {
      trackEvent('MEETING_VIEWED', {
        meeting_id: meetingId,
        meeting_title: item.title,
        has_calendar_event: item.hasCalendarEvent,
      });
    }

    const tab: 'notes' | 'transcript' = item.event ? 'notes' : 'transcript';
    const search = item.event && openPrep ? MEETING_PREP_READY_SEARCH : { tab };
    if (event && isNewTabClick(event)) {
      openTab({ path: `/meetings/${meetingId}`, search });
      return;
    }
    void navigate({
      to: '/meetings/$meetingId',
      params: { meetingId },
      search,
    });
  };
};

export const MeetingsSettingsButton = () => (
  <SidePanel.Action
    icon={Icons.settings}
    label="Meeting settings"
    onClick={() => openSettings('Meetings')}
  />
);

export const MeetingsTimelineSidebar = observer(() => {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const { live, nextItem, projectIds, hasUnassigned } = useMeetingsAgenda();
  const { meetings } = useStores();
  const nextMeeting = nextItem
    ? getAgendaItemMeeting(nextItem, meetings)
    : undefined;
  const isNextUpMeetingOpen =
    nextMeeting !== undefined &&
    nextMeeting.id === meetingStore.meetingInViewId;

  return (
    <div className="contents">
      <SidePanel.PrimaryAction>
        <YStack className="gap-0.5">
          <SidebarRow.Button
            leading={<Icons.home className="size-3.5" />}
            selected={
              pathname === '/meetings' && !meetingsSidebarStore.hasSlice
            }
            onClick={() => {
              meetingsSidebarStore.clearSlice();
              void navigate({ to: '/meetings' });
            }}
          >
            Home
          </SidebarRow.Button>
          {isCapable(AppCapability.RecordMeeting) && <NewNoteRow />}
        </YStack>
      </SidePanel.PrimaryAction>

      <YStack className="min-h-0 flex-1 overflow-y-auto scrollbar-hide">
        <ProjectsSection
          upcomingProjectIds={projectIds}
          hasUnassignedUpcoming={hasUnassigned}
        />
        <CirclesSections />
      </YStack>

      {live.length === 0 && nextItem && !isNextUpMeetingOpen && (
        <YStack className="shrink-0 pb-3 pt-3">
          <NextUpCard item={nextItem} />
        </YStack>
      )}

      {live.length > 0 && (
        <YStack className="shrink-0 gap-2 pb-3 pt-2">
          {live.map((item) => (
            <LiveCard key={item.key} item={item} />
          ))}
        </YStack>
      )}
    </div>
  );
});

const NewNoteRow = observer(() => (
  <SidebarRow.Button
    leading={
      meetingStore.isStartingMeeting ? (
        <Icons.loader className="size-3.5 animate-spin" />
      ) : (
        <Icons.mic className="size-3.5" />
      )
    }
    disabled={isNewNoteDisabled()}
    onClick={() => startMeetingFromOverview()}
  >
    New note
  </SidebarRow.Button>
));

const EnsureProjectLoaded = ({ projectId }: { projectId: string }) => {
  useEnsureProjectLoaded(projectId);
  return null;
};

const useStepReveal = (total: number) => {
  const [visibleCount, setVisibleCount] = useState(SECTION_LIMIT);
  const hasMore = total > visibleCount;
  return {
    visibleCount,
    hasMore,
    canStep: total > SECTION_LIMIT,
    step: () =>
      setVisibleCount(hasMore ? visibleCount + SECTION_PAGE : SECTION_LIMIT),
    reset: () => setVisibleCount(SECTION_LIMIT),
  };
};

const ShowMoreRow = ({
  hasMore,
  onClick,
  size,
}: {
  hasMore: boolean;
  onClick: () => void;
  size?: 'lg';
}) => (
  <SidebarRow.Root asChild size={size} muted>
    <button type="button" onClick={onClick}>
      {hasMore ? 'Show more' : 'Show less'}
    </button>
  </SidebarRow.Root>
);

const ProjectsViewOptionsMenu = ({
  withNotesOnly,
  onWithNotesOnlyChange,
}: {
  withNotesOnly: boolean;
  onWithNotesOnlyChange: (withNotesOnly: boolean) => void;
}) => (
  <DropdownMenu.Root>
    <DropdownMenu.Trigger asChild>
      <RoundIconButton
        label="View options"
        icon={Icons.slidersHorizontal}
        size="xs"
        active={withNotesOnly}
        data-active={withNotesOnly}
        className="-my-0.5"
      />
    </DropdownMenu.Trigger>
    <DropdownMenu.Content align="end" className="w-48">
      <DropdownMenu.Label>Show projects</DropdownMenu.Label>
      <DropdownMenu.RadioGroup
        value={withNotesOnly ? 'with-notes' : 'all'}
        onValueChange={(value) => onWithNotesOnlyChange(value === 'with-notes')}
      >
        <DropdownMenu.RadioItem value="all">All</DropdownMenu.RadioItem>
        <DropdownMenu.RadioItem value="with-notes">
          With meeting notes
        </DropdownMenu.RadioItem>
      </DropdownMenu.RadioGroup>
    </DropdownMenu.Content>
  </DropdownMenu.Root>
);

const ProjectsSection = observer(
  ({
    upcomingProjectIds,
    hasUnassignedUpcoming,
  }: {
    upcomingProjectIds: string[];
    hasUnassignedUpcoming: boolean;
  }) => {
    const { projects } = useStores();
    const navigate = useNavigate();
    const { openTab } = useTabs();
    const {
      projectIds: meetingProjectIds,
      hasUnassignedMeetings: hasUnassignedNotes,
    } = useMeetingCircles();
    const hasUnassignedMeetings = hasUnassignedNotes || hasUnassignedUpcoming;
    const { projectsExpanded, projectSlice, projectsWithNotesOnly } =
      meetingsSidebarStore;
    useLoadAllProjectPages(projectsExpanded);

    const notedProjectIds = [
      ...new Set([...upcomingProjectIds, ...meetingProjectIds]),
    ];
    const notedProjectIdSet = new Set(notedProjectIds);
    const otherProjectIds = [
      ...projects.pinnedProjects,
      ...projects.recentProjects,
    ]
      .map((project) => project.id)
      .filter((projectId) => !notedProjectIdSet.has(projectId));
    const allProjectIds = [...notedProjectIds, ...otherProjectIds];
    const projectIds = projectsWithNotesOnly
      ? allProjectIds.filter(
          (projectId) =>
            notedProjectIdSet.has(projectId) || projectId === projectSlice
        )
      : allProjectIds;
    const reveal = useStepReveal(projectIds.length);
    const isEmptyFilterResult =
      projectsWithNotesOnly && projectIds.length === 0;

    const openProject = (projectId: string, event: React.MouseEvent) => {
      if (isNewTabClick(event)) {
        openTab({ path: `/projects/${projectId}` });
        return;
      }
      void navigate({ to: '/projects/$projectId', params: { projectId } });
    };

    const selectSlice = (slice: string) => {
      meetingsSidebarStore.selectProjectSlice(slice);
      void navigate({ to: '/meetings' });
    };

    const cappedIds = projectIds.slice(0, reveal.visibleCount);
    const isSelectionCapped =
      projectSlice !== null &&
      projectIds.includes(projectSlice) &&
      !cappedIds.includes(projectSlice);
    const visibleIds = isSelectionCapped
      ? [...cappedIds, projectSlice]
      : cappedIds;
    const visibleProjects = visibleIds.flatMap((projectId) => {
      const project = projects.getById(projectId);
      return project ? [project] : [];
    });

    if (allProjectIds.length === 0 && !hasUnassignedMeetings) return null;

    return (
      <>
        {visibleIds.map((projectId) => (
          <EnsureProjectLoaded key={projectId} projectId={projectId} />
        ))}
        <YStack className={cn('gap-0.5', SECTION_GAP)}>
          <SidebarSectionHeading
            isExpanded={projectsExpanded}
            onExpandedChange={(expanded) => {
              if (!expanded) reveal.reset();
              meetingsSidebarStore.setProjectsExpanded(expanded);
            }}
            rightSlot={
              <ProjectsViewOptionsMenu
                withNotesOnly={projectsWithNotesOnly}
                onWithNotesOnlyChange={(withNotesOnly) => {
                  reveal.reset();
                  meetingsSidebarStore.setProjectsWithNotesOnly(withNotesOnly);
                }}
              />
            }
          >
            Projects
          </SidebarSectionHeading>
          {projectsExpanded && isEmptyFilterResult && (
            <SidebarRow.Root interactive={false} muted className="text-xs">
              No projects with meeting notes
            </SidebarRow.Root>
          )}
          {projectsExpanded &&
            visibleProjects.map((project) => (
              <SidebarRow.Root
                key={project.id}
                role="button"
                tabIndex={0}
                selected={projectSlice === project.id}
                className="group/meetings-project"
                onClick={() => selectSlice(project.id)}
                onKeyDown={onActivateKey(() => selectSlice(project.id))}
              >
                <SidebarRow.Leading>
                  <SidebarRow.Icon>
                    <EntityIcon
                      icon={getProjectSidebarIcon(project, false)}
                      size="100%"
                    />
                  </SidebarRow.Icon>
                </SidebarRow.Leading>
                <SidebarRow.Label>{project.name}</SidebarRow.Label>
                <RoundIconButton
                  label={`Open ${project.name}`}
                  icon={Icons.arrowUpRight}
                  size="xs"
                  className="hidden group-hover/meetings-project:inline-flex"
                  onClick={(event) => {
                    event.stopPropagation();
                    openProject(project.id, event);
                  }}
                />
              </SidebarRow.Root>
            ))}
          {projectsExpanded && hasUnassignedMeetings && (
            <SidebarRow.Button
              selected={projectSlice === NO_PROJECT_SLICE}
              leading={<Icons.circleDashed className="size-3.5" />}
              onClick={() => selectSlice(NO_PROJECT_SLICE)}
            >
              No project
            </SidebarRow.Button>
          )}
          {projectsExpanded && reveal.canStep && (
            <ShowMoreRow hasMore={reveal.hasMore} onClick={reveal.step} />
          )}
        </YStack>
      </>
    );
  }
);

const nextUpDay = (startsAt: Date) => {
  if (isToday(startsAt)) return null;
  if (isTomorrow(startsAt)) return 'Tomorrow';
  return format(startsAt, 'EEE');
};

const CircleSection = observer(
  ({
    title,
    circles,
    isExpanded,
    onExpandedChange,
  }: {
    title: string;
    circles: MeetingCircle[];
    isExpanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
  }) => {
    const navigate = useNavigate();
    const hideMeetingCircle = useHideMeetingCircle();
    const reveal = useStepReveal(circles.length);
    const visible = circles.slice(0, reveal.visibleCount);
    const { circleKey } = meetingsSidebarStore;

    if (circles.length === 0) return null;

    return (
      <YStack className={cn('gap-0.5', SECTION_GAP)}>
        <SidebarSectionHeading
          isExpanded={isExpanded}
          onExpandedChange={(expanded) => {
            if (!expanded) reveal.reset();
            onExpandedChange(expanded);
          }}
        >
          {title}
        </SidebarSectionHeading>
        {isExpanded &&
          visible.map((circle) => (
            <ContextMenu key={circle.key}>
              <ContextMenuTrigger asChild>
                <SidebarRow.Button
                  size="lg"
                  selected={circleKey === circle.key}
                  title={`${circle.meetingIds.length} ${pluralize(circle.meetingIds.length, 'meeting')}`}
                  leading={<CircleFaces circle={circle} size="sm" />}
                  onClick={() => {
                    meetingsSidebarStore.selectCircle(circle.key);
                    void navigate({ to: '/meetings' });
                  }}
                >
                  {circle.name}
                </SidebarRow.Button>
              </ContextMenuTrigger>
              <ContextMenuContent className="min-w-40">
                <ContextMenuItem
                  icon={Icons.eyeOff}
                  onClick={() => hideMeetingCircle(circle)}
                >
                  Hide from {title}
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          ))}
        {isExpanded && reveal.canStep && (
          <ShowMoreRow
            size="lg"
            hasMore={reveal.hasMore}
            onClick={reveal.step}
          />
        )}
      </YStack>
    );
  }
);

const CirclesSections = observer(() => {
  const { people, recurring } = useMeetingCircles();

  return (
    <>
      <CircleSection
        title="Recurring"
        circles={recurring}
        isExpanded={meetingsSidebarStore.recurringExpanded}
        onExpandedChange={meetingsSidebarStore.setRecurringExpanded}
      />
      <CircleSection
        title="Circles"
        circles={people}
        isExpanded={meetingsSidebarStore.circlesExpanded}
        onExpandedChange={meetingsSidebarStore.setCirclesExpanded}
      />
    </>
  );
});

const NextUpCard = observer(({ item }: { item: AgendaItem }) => {
  const { meetings } = useStores();
  const open = useOpenAgendaItem();
  const nowMs = useNow();
  const meeting = getAgendaItemMeeting(item, meetings);
  const brief =
    (meeting?.prep && markdownToExcerpt(meeting.prep)) || item.attendeesLabel;
  const startsAtMs = item.startsAt.getTime();
  const endsAtMs = item.endsAt?.getTime() ?? startsAtMs;
  const isHappeningNow = startsAtMs <= nowMs && endsAtMs >= nowMs;
  const minutesUntilStart = Math.ceil((startsAtMs - nowMs) / 60_000);
  const day = nextUpDay(item.startsAt);
  const joinUrl =
    nowMs >= startsAtMs - JOIN_WINDOW_MS && endsAtMs >= nowMs
      ? item.joinUrl
      : null;

  return (
    <YStack
      role="button"
      tabIndex={0}
      onClick={(event) => void open(item, event)}
      onKeyDown={onActivateKey(() => void open(item))}
      className="cursor-default gap-1 rounded-xl border border-border bg-surface px-3.5 py-3 shadow-sm transition-colors hover:bg-surface-hover focus-ring"
    >
      {isHappeningNow ? (
        <span className="flex items-center gap-1.5 text-[0.6875rem] uppercase tracking-wider text-brand-emerald-green">
          <span className="size-1.5 rounded-full bg-brand-emerald-green" />
          Now
          {item.endsAt && (
            <>
              {' · until '}
              <Time date={item.endsAt} />
            </>
          )}
        </span>
      ) : (
        <span className="text-[0.6875rem] uppercase tracking-wider text-foreground-muted">
          Next · {day && `${day} · `}
          <Time date={item.startsAt} />
          {minutesUntilStart > 0 &&
            minutesUntilStart < 60 &&
            ` · in ${minutesUntilStart} min`}
        </span>
      )}
      <span className="truncate text-sm font-medium leading-5 first-letter:uppercase">
        {item.title}
      </span>
      {brief && (
        <span className="line-clamp-2 text-xs text-foreground-muted">
          {brief}
        </span>
      )}
      <XStack className="mt-2 gap-1.5">
        {joinUrl && item.event && (
          <JoinButton
            event={item.event}
            joinUrl={joinUrl}
            className="border-transparent bg-foreground text-background hover:bg-foreground/85 [&>svg]:text-background"
          />
        )}
        <RoundButton
          className="h-7 border-border bg-surface-active px-3 text-xs hover:bg-surface-hover"
          onClick={(event) => {
            event.stopPropagation();
            void open(item, event, { openPrep: true });
          }}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {item.status === 'prep-ready' ? 'Open prep' : 'Prep'}
        </RoundButton>
      </XStack>
    </YStack>
  );
});

const LiveCard = ({ item }: { item: AgendaItem }) => {
  const open = useOpenAgendaItem();
  const updateWaveRef = useRef<(levels: MeetingAudioEnergyLevel) => void>(
    () => null
  );

  useEffect(
    () =>
      meetingStore.subscribeAudioEnergyLevel((levels) => {
        updateWaveRef.current?.(levels);
      }),
    []
  );

  return (
    <button
      type="button"
      onClick={(event) => void open(item, event)}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-xl p-1.5 pr-2.5 text-left transition-colors focus-ring',
        'border border-brand-jordy-blueA/40 bg-brand-jordy-blueA/10 hover:bg-brand-jordy-blueA/20',
        'dark:border-brand-jordy-blueA/15'
      )}
    >
      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-jordy-blueA/15">
        <AnimatedAudiowave
          updateWaveRef={updateWaveRef}
          maxLength={14}
          barColor="bg-brand-jordy-blue"
          gap="2px"
          barWidth="2px"
        />
      </span>
      <YStack className="min-w-0 flex-1">
        <span className="truncate text-sm font-medium leading-5 first-letter:uppercase">
          {item.title}
        </span>
        <span className="flex items-center gap-1 text-xs text-foreground-muted">
          Live ·
          <AnimatedTimer
            startDate={item.startsAt}
            className="tabular-nums text-foreground-muted"
          />
        </span>
      </YStack>
      <Icons.chevronRight
        aria-hidden
        className="size-3.5 shrink-0 text-foreground-muted"
      />
    </button>
  );
};

const JoinButton = ({
  event,
  joinUrl,
  className,
}: {
  event: CalendarEvent;
  joinUrl: string;
  className?: string;
}) => (
  <RoundButton
    icon={Icons.video}
    className={cn('h-7 self-center gap-1.5 pl-2.5 pr-3 text-xs', className)}
    onClick={(clickEvent) => {
      clickEvent.stopPropagation();
      trackEvent('MEETINGS_SIDEBAR_JOIN_CLICKED', { event_id: event.id });
      if (!inElectron()) {
        window.open(joinUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      performScheduleJoin(
        {
          joinUrl,
          eventId: event.id,
          title: event.summary,
          canRecord: isCapable(AppCapability.RecordMeeting),
        },
        {
          openExternal: (url) =>
            electronApis.system.openExternal(url, { preferMeetingApp: true }),
          markUpcomingMeetingJoined: (eventId) =>
            void electronApis.meetings.markUpcomingMeetingJoined(eventId),
          startMeetingNote: (input) =>
            electronApis.meetings.requestStartMeetingNote(input),
        }
      );
    }}
    onKeyDown={(keyEvent) => keyEvent.stopPropagation()}
  >
    Join
  </RoundButton>
);
